import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDIContext } from '@/Framework/DI/DIContext';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';
import { prepareTopPeriodsPayload } from '@/finsight/application/TopCompanies/helpers';
import CompanyRepository from '@/finsight/infrastructure/repository/CompanyRepository';
import useExportButtonWithSpinner from './useExportButtonWithSpinner';
import { useDictionariesContext } from '@/finsight/application/Dictionaries/DictionariesContext';
import { ISSUER_NAME } from '@/finsight/ui/common/components/TopCompanies/constants';
import { IParent } from '@/finsight/domain/vo/IDeal';

interface IPeriod {
  dateFrom: string,
  dateTo: string | null,
  disabled: boolean,
  label: string,
  value: string,
}

interface ITopParents {
  periods: {
    collection: IPeriod[],
    hasServerError: boolean,
  },
  showExport: boolean,
  parent: IParent,
}

export const useTopCompanyCommon = ({
  periods: {
    collection: serverPeriodsCollection,
    hasServerError,
  },
  showExport: serverShowExport,
  parent,
}: ITopParents) => {
  const { container } = useDIContext();
  const {
    isExportFetching,
    isExportAvailable,
    downloadExcelFile,
    setIsExportAvailable,
  } = useExportButtonWithSpinner(serverShowExport);
  const { regionsList, productsList } = useDictionariesContext();
  const [periodsCollection, setPeriodsCollection] = useState(serverPeriodsCollection);
  const [isPeriodsFetching, setIsPeriodsFetching] = useState<boolean>(true);
  const [selectedPeriod, setSelectedPeriod] = useState<IPeriod>(serverPeriodsCollection[0] || null);
  const [activeTab, setActiveTab] = useState<string>(ISSUER_NAME);
  const { pageFilters } = usePageFiltersContext();

  const payload = prepareTopPeriodsPayload({
    ...pageFilters,
  });

  const getPeriodsCollection = async (): Promise<void> => {
    const companyRepository = container.get(CompanyRepository);
    setIsPeriodsFetching(false);
    try {
      const response = await companyRepository.getTopCompaniesPeriods(payload);
      setPeriodsCollection(response.map((filter) => ({
        value: filter.filterName,
        label: filter.filterName,
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        disabled: !filter.active,
      })));
    } finally {
      setIsPeriodsFetching(true);
    }
  };

  useEffect(() => {
    if (hasServerError) {
      getPeriodsCollection();
    }
  }, [hasServerError]);

  useEffect(() => {
    if (!hasServerError) {
      setPeriodsCollection(serverPeriodsCollection);
      setSelectedPeriod(serverPeriodsCollection[0] || null);
    }
  }, [serverPeriodsCollection]);

  const exportToExcel = () => downloadExcelFile({
    pageFilters,
    selectedPeriod,
    products: productsList,
    regions: regionsList,
    parentName: parent?.name,
  });

  return {
    isPeriodsFetching,
    periodsCollection,
    setSelectedPeriod,
    selectedPeriod,
    isExportFetching,
    isExportAvailable,
    setIsExportAvailable,
    exportToExcel,
    activeTab,
    setActiveTab,
  };
};

type TopCompanyCommonContextType = ReturnType<typeof useTopCompanyCommon>;

export const TopCompanyCommonContext = createContext<TopCompanyCommonContextType>(null);

export const useTopCompanyCommonContext = () => {
  const context = useContext(TopCompanyCommonContext);
  if (!context) {
    throw new Error('useTopCompanyCommonContext must be used within the TopCompanyCommonContext');
  }
  return context;
};

interface IProps {
  topParents: ITopParents,
  children: React.ReactNode,
}

const TopCompanyCommonContextProvider = ({ children, topParents }: IProps) => (
  <TopCompanyCommonContext.Provider value={ useTopCompanyCommon(topParents) }>
    { children }
  </TopCompanyCommonContext.Provider>
);

export default TopCompanyCommonContextProvider;
